/**
 * Shared password validation & expiry logic.
 * Zero dependencies — safe to import from both server and client.
 */

export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_EXPIRY_DAYS = 365;

interface PasswordValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Checks a password against complexity rules:
 * - At least 10 characters
 * - Contains at least one letter
 * - Contains at least one number
 * - Contains at least one special character
 */
export function validatePasswordComplexity(password: string): PasswordValidationResult {
  const errors: string[] = [];

  if (password.length < PASSWORD_MIN_LENGTH) {
    errors.push(`Must be at least ${PASSWORD_MIN_LENGTH} characters`);
  }
  if (!/[a-zA-Z]/.test(password)) {
    errors.push("Must contain at least one letter");
  }
  if (!/[0-9]/.test(password)) {
    errors.push("Must contain at least one number");
  }
  if (!/[^a-zA-Z0-9]/.test(password)) {
    errors.push("Must contain at least one special character");
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Individual rule checks for real-time UI feedback.
 */
export const PASSWORD_RULES = [
  { id: "length", label: `At least ${PASSWORD_MIN_LENGTH} characters`, test: (p: string) => p.length >= PASSWORD_MIN_LENGTH },
  { id: "letter", label: "Contains a letter", test: (p: string) => /[a-zA-Z]/.test(p) },
  { id: "number", label: "Contains a number", test: (p: string) => /[0-9]/.test(p) },
  { id: "special", label: "Contains a special character", test: (p: string) => /[^a-zA-Z0-9]/.test(p) },
] as const;

/**
 * Returns true if the user's password has expired (older than PASSWORD_EXPIRY_DAYS).
 * Falls back to createdAt when passwordChangedAt is null (user never changed password).
 */
export function isPasswordExpired(
  passwordChangedAt: Date | string | null,
  createdAt: Date | string,
): boolean {
  const referenceDate = passwordChangedAt ? new Date(passwordChangedAt) : new Date(createdAt);
  const now = new Date();
  const diffMs = now.getTime() - referenceDate.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  return diffDays >= PASSWORD_EXPIRY_DAYS;
}

/**
 * Returns the number of days until the password expires.
 * Negative values mean the password is already expired.
 */
export function daysUntilPasswordExpiry(
  passwordChangedAt: Date | string | null,
  createdAt: Date | string,
): number {
  const referenceDate = passwordChangedAt ? new Date(passwordChangedAt) : new Date(createdAt);
  const now = new Date();
  const diffMs = now.getTime() - referenceDate.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  return Math.ceil(PASSWORD_EXPIRY_DAYS - diffDays);
}
